import React, { useState, useEffect, useCallback } from "react";
import { Calendar, Package, Wallet, Banknote, ClipboardList, TrendingUp, Trophy } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { statsAPI } from "../api";
import "./Stats.css";

const BreederStats = () => {
  const { isBreeder } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Période par défaut : 6 derniers mois
  const getDefaultStartDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 6);
    return date.toISOString().split('T')[0];
  };
  
  const getDefaultEndDate = () => {
    return new Date().toISOString().split('T')[0];
  };
  
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        start_date: startDate,
        end_date: endDate,
      };
      const response = await statsAPI.getBreederStats(params);
      setStats(response.data);
    } catch (err) {
      setError("Erreur lors du chargement des statistiques");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (isBreeder) {
      loadStats();
    }
  }, [isBreeder, loadStats]);
  
  const handlePeriodChange = () => {
    loadStats();
  };
  
  const resetPeriod = () => {
    setStartDate(getDefaultStartDate());
    setEndDate(getDefaultEndDate());
  };

  if (!isBreeder) {
    return (
      <div className="container main-content">
        <div className="alert alert-error">
          Vous devez être éleveur pour voir cette page.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container main-content">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container main-content">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const statusLabels = {
    pending: "En attente",
    confirmed: "Confirmée",
    preparing: "En préparation",
    ready: "Prête",
    shipped: "Expédiée",
    delivered: "Livrée",
    cancelled: "Annulée",
  };

  return (
    <div className="container main-content">
      <div className="page-header">
        <h1>Mes Statistiques</h1>
        <p>Vue d'ensemble de vos produits et ventes</p>
      </div>

      {/* Sélecteur de période */}
      <div className="card" style={{ marginBottom: "2rem" }}>
        <h3 className="icon-title" style={{ marginBottom: "1rem" }}><Calendar size={18} strokeWidth={2.25} /> Sélectionner la période</h3>
        <div className="stats-period-picker">
          <div className="form-group">
            <label htmlFor="start_date" className="form-label">
              Date de début
            </label>
            <input
              type="date"
              id="start_date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              onBlur={handlePeriodChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="end_date" className="form-label">
              Date de fin
            </label>
            <input
              type="date"
              id="end_date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              onBlur={handlePeriodChange}
            />
          </div>
          <button
            onClick={resetPeriod}
            className="btn btn-outline btn-sm"
            style={{ height: "fit-content" }}
          >
            Réinitialiser
          </button>
        </div>
      </div>

      <div className="stats-grid">
        {/* Produits */}
        <div className="stat-card card">
          <h3 className="icon-title"><Package size={18} strokeWidth={2.25} /> Produits</h3>
          <div className="stat-value">{stats.products.total}</div>
          <div className="stat-details">
            <div className="stat-detail">
              <span className="stat-label">Disponibles</span>
              <span className="stat-number">{stats.products.available}</span>
            </div>
            <div className="stat-detail">
              <span className="stat-label">Stock total</span>
              <span className="stat-number">{stats.products.total_stock}</span>
            </div>
          </div>
        </div>

        {/* Ventes */}
        <div className="stat-card card">
          <h3 className="icon-title"><Wallet size={18} strokeWidth={2.25} /> Ventes</h3>
          <div className="stat-value">{stats.sales.total_orders}</div>
          <div className="stat-label">Commandes totales</div>
          <div className="stat-revenue">
            {stats.sales.total_revenue.toLocaleString("fr-FR")} F CFA
          </div>
        </div>

        {/* Revenus */}
        <div className="stat-card card">
          <h3 className="icon-title"><Banknote size={18} strokeWidth={2.25} /> Revenus totaux</h3>
          <div className="stat-value-large">
            {stats.sales.total_revenue.toLocaleString("fr-FR")} F CFA
          </div>
        </div>

        {/* Commandes par statut */}
        <div className="stat-card card">
          <h3 className="icon-title"><ClipboardList size={18} strokeWidth={2.25} /> Commandes par statut</h3>
          <div className="status-list">
            {Object.entries(stats.sales.orders_by_status).map(([status, count]) => (
              <div key={status} className="status-item">
                <span className="status-label">{statusLabels[status] || status}</span>
                <span className="status-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Revenus par mois */}
      <div className="card" style={{ marginTop: "2rem" }} data-tour="stats-chart">
        <h3 className="icon-title"><TrendingUp size={18} strokeWidth={2.25} /> Revenus par mois</h3>
        <p style={{ fontSize: "0.9rem", color: "var(--text-secondary)", marginBottom: "1rem" }}>
          Période : {new Date(startDate).toLocaleDateString("fr-FR")} - {new Date(endDate).toLocaleDateString("fr-FR")}
        </p>
        <div className="chart-container">
          {stats.revenue_by_month && stats.revenue_by_month.length > 0 ? (
            <div className="line-chart">
              <svg className="line-chart-svg" viewBox="0 0 800 300" preserveAspectRatio="none">
                {(() => {
                  const maxRevenue = Math.max(...stats.revenue_by_month.map(r => r.revenue || 0), 1);
                  const minRevenue = Math.min(...stats.revenue_by_month.map(r => r.revenue || 0), 0);
                  const range = maxRevenue - minRevenue || 1;
                  const width = 800;
                  const height = 300;
                  const padding = 40;
                  const chartWidth = width - 2 * padding;
                  const chartHeight = height - 2 * padding;
                  const stepX = stats.revenue_by_month.length > 1 
                    ? chartWidth / (stats.revenue_by_month.length - 1)
                    : chartWidth;
                
                  // Créer les points
                  const points = stats.revenue_by_month.map((item, index) => {
                    const revenue = item.revenue || 0;
                    const x = padding + (stats.revenue_by_month.length > 1 ? index * stepX : chartWidth / 2);
                    const y = padding + chartHeight - ((revenue - minRevenue) / range) * chartHeight;
                    return { x, y, value: revenue, month: item.month };
                  });
                
                  // Créer le chemin de la ligne
                  const pathData = points.length > 1
                    ? points.map((point, index) => 
                        `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
                      ).join(' ')
                    : `M ${points[0].x} ${points[0].y} L ${points[0].x} ${points[0].y}`;
                  
                  // Créer la zone sous la courbe
                  const areaPath = points.length > 1
                    ? `${pathData} L ${points[points.length - 1].x} ${height - padding} L ${points[0].x} ${height - padding} Z`
                    : `M ${points[0].x} ${height - padding} L ${points[0].x} ${points[0].y} L ${points[0].x} ${height - padding} Z`;
                  
                  return (
                    <>
                      {/* Zone sous la courbe */}
                      <path
                        d={areaPath}
                        fill="url(#gradient-breeder)"
                        opacity="0.3"
                      />
                      {/* Ligne */}
                      {points.length > 1 && (
                        <path
                          d={pathData}
                          fill="none"
                          stroke="var(--success)"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      )}
                      {/* Points */}
                      {points.map((point, index) => (
                        <g key={index}>
                          <circle
                            cx={point.x}
                            cy={point.y}
                            r="5"
                            fill="var(--success)"
                            stroke="white"
                            strokeWidth="2"
                          />
                          <title>{point.month}: {point.value.toLocaleString("fr-FR")} F CFA</title>
                        </g>
                      ))}
                      {/* Définir le gradient */}
                      <defs>
                        <linearGradient id="gradient-breeder" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="var(--success)" stopOpacity="0.5" />
                          <stop offset="100%" stopColor="var(--success)" stopOpacity="0.1" />
                        </linearGradient>
                      </defs>
                    </>
                  );
                })()}
              </svg>
            {/* Labels des mois */}
            <div className="line-chart-labels">
              {stats.revenue_by_month.map((item, index) => (
                <div key={index} className="line-chart-label">
                  {item.month}
                </div>
              ))}
            </div>
              {/* Légende des valeurs */}
              {(() => {
                const maxRevenue = Math.max(...stats.revenue_by_month.map(r => r.revenue || 0), 1);
                const minRevenue = Math.min(...stats.revenue_by_month.map(r => r.revenue || 0), 0);
                const range = maxRevenue - minRevenue || 1;
                const height = 300;
                const padding = 40;
                const chartHeight = height - 2 * padding;
                const chartWidth = 800 - 2 * padding;
                const stepX = stats.revenue_by_month.length > 1 
                  ? chartWidth / (stats.revenue_by_month.length - 1)
                  : chartWidth / 2;
                
                return (
                  <div className="line-chart-values">
                    {stats.revenue_by_month.map((item, index) => {
                      const revenue = item.revenue || 0;
                      const x = padding + (stats.revenue_by_month.length > 1 ? index * stepX : chartWidth / 2);
                      const y = padding + chartHeight - ((revenue - minRevenue) / range) * chartHeight;
                      
                      return (
                        <div
                          key={index}
                          className="line-chart-value"
                          style={{
                            left: `${(x / 800) * 100}%`,
                            top: `${(y / 300) * 100}%`,
                            color: "var(--success)",
                          }}
                        >
                          {revenue > 0 && (
                            <span>{revenue.toLocaleString("fr-FR")} F CFA</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          ) : (
            <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>
              <p>Aucune donnée de revenus disponible pour cette période</p>
            </div>
          )}
        </div>
      </div>

      {/* Produits les plus vendus */}
      {stats.top_products && stats.top_products.length > 0 && (
        <div className="card" style={{ marginTop: "2rem" }}>
          <h3 className="icon-title"><Trophy size={18} strokeWidth={2.25} /> Produits les plus vendus</h3>
          <div className="top-products-list">
            {stats.top_products.map((product, index) => (
              <div key={index} className="top-product-item">
                <div className="top-product-rank">#{index + 1}</div>
                <div className="top-product-info">
                  <div className="top-product-name">{product.name}</div>
                  <div className="top-product-details">
                    <span>Quantité: {product.quantity}</span>
                    <span>Revenus: {product.revenue.toLocaleString("fr-FR")} F CFA</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BreederStats;

